import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { IMAGES } from "@/data/site";

export const SITE_NAME = "Entre Les Mondes";
export const SITE_URL = process.env.REACT_APP_PUBLIC_URL.replace(/\/$/, "");
export const ORGANISATION = {
  "@type": "LocalBusiness",
  "@id": `${SITE_URL}/#organisation`,
  name: SITE_NAME,
  url: SITE_URL,
  telephone: "+41794606595",
  email: "ludo@entrelesmondes.net",
  address: { "@type": "PostalAddress", streetAddress: "Rue Pierre Viret 8", postalCode: "1350", addressLocality: "Orbe", addressRegion: "Vaud", addressCountry: "CH" },
};
export const AUTEUR = { "@type": "Person", name: "Ludovic Isoz", url: `${SITE_URL}/a-propos`, jobTitle: "Artisan de tambours & praticien énergétique" };
export const ATELIER_PLACE = { "@type": "Place", name: "Atelier Entre Les Mondes", address: ORGANISATION.address };

const setMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!content) { el?.remove(); return; }
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute("content", content);
};

const setLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
  el.setAttribute("href", href);
};

const setJsonLd = (data) => {
  let el = document.getElementById("seo-jsonld");
  if (!data) { el?.remove(); return; }
  if (!el) { el = document.createElement("script"); el.type = "application/ld+json"; el.id = "seo-jsonld"; document.head.appendChild(el); }
  el.textContent = JSON.stringify({ "@context": "https://schema.org", ...(Array.isArray(data) ? { "@graph": data } : data) });
};

export const absolute = (path) => `${SITE_URL}${path}`;

export const breadcrumb = (items) => ({
  "@type": "BreadcrumbList",
  itemListElement: items.map(([name, path], i) => ({ "@type": "ListItem", position: i + 1, name, item: absolute(path) })),
});

export const eventJsonLd = (e) => ({
  "@type": "Event",
  name: e.titre,
  description: e.description,
  startDate: e.date,
  endDate: e.date_fin || undefined,
  eventStatus: "https://schema.org/EventScheduled",
  eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
  location: { "@type": "Place", name: e.lieu, address: e.lieu },
  image: e.image || IMAGES.cercle,
  organizer: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
  performer: AUTEUR,
  offers: e.prix != null ? { "@type": "Offer", price: e.prix, priceCurrency: "CHF", availability: e.places_illimitees || e.places_restantes > 0 ? "https://schema.org/InStock" : "https://schema.org/SoldOut", url: absolute(e.slug ? `/evenements/${e.slug}` : "/agenda"), validFrom: e.date.slice(0, 10) } : undefined,
});

export const productJsonLd = (p) => ({
  "@type": "Product",
  name: `Tambour ${p.nom} ${p.numero}`,
  description: p.description,
  image: p.image || undefined,
  sku: p.numero,
  brand: { "@type": "Brand", name: SITE_NAME },
  material: `Peau de ${p.peau}, cadre en bois`,
  offers: { "@type": "Offer", price: p.prix, priceCurrency: "CHF", availability: p.disponible ? "https://schema.org/InStock" : "https://schema.org/SoldOut", url: absolute(`/boutique/tambour/${p.id}`), seller: { "@type": "Organization", name: SITE_NAME } },
});

export const useNoIndex = () => {
  useEffect(() => {
    setMeta("name", "robots", "noindex, nofollow");
    document.head.querySelector('link[rel="canonical"]')?.remove();
    setJsonLd(null);
  }, []);
};

export const useSeo = ({ title, description, image = IMAGES.hero, type = "website", jsonLd = null, noindex = false, canonicalPath = null }) => {
  const { pathname } = useLocation();
  const ld = jsonLd ? JSON.stringify(jsonLd) : "";
  useEffect(() => {
    if (!title) return;
    const url = absolute(canonicalPath || pathname);
    document.title = title;
    setMeta("name", "description", description);
    setMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large");
    setLink("canonical", url);
    setMeta("property", "og:site_name", SITE_NAME);
    setMeta("property", "og:locale", "fr_CH");
    setMeta("property", "og:type", type);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", image);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", image);
    setJsonLd(ld ? JSON.parse(ld) : null);
  }, [title, description, image, type, noindex, pathname, canonicalPath, ld]);
};
